import type { TaxSavingsSimulatorFormSchema } from './schema';

import {
  DECOTE,
  PER_DEDUCTION,
  QUOTIENT_CAP_PER_HALF_PART,
  TAX_BRACKETS,
  TAX_RECOVERY_THRESHOLD,
} from './constants';

/**
 * Calculates the total income tax based on French tax system rules.
 * @param householdAnnualIncome The total annual income of the household.
 * @param taxSharesHousehold The number of tax shares ("parts fiscales") for the household.
 * @returns The total tax amount before any deductions.
 */
const calculateTotalTax = ({
  householdAnnualIncome,
  taxSharesHousehold,
}: TaxSavingsSimulatorFormSchema): number => {
  const familyQuotient = householdAnnualIncome / taxSharesHousehold;

  let totalTax = 0;
  for (const bracket of TAX_BRACKETS) {
    if (familyQuotient >= bracket.min) {
      const taxableAmount =
        Math.min(familyQuotient, bracket.max) - (bracket.min - 1);
      totalTax += taxableAmount * bracket.rate;
    }
  }
  return totalTax * taxSharesHousehold;
};

/**
 * Calculates the TMI (Tranche Marginale d'Imposition / marginal tax rate).
 * @param householdAnnualIncome The total annual income of the household.
 * @param taxSharesHousehold The number of tax shares ("parts fiscales") for the household.
 * @returns The highest applicable tax rate for the household based on family quotient.
 */
const calculateTmi = ({
  householdAnnualIncome,
  taxSharesHousehold,
}: TaxSavingsSimulatorFormSchema): number => {
  const familyQuotient = householdAnnualIncome / taxSharesHousehold;

  for (let i = TAX_BRACKETS.length - 1; i >= 0; i--) {
    const bracket = TAX_BRACKETS[i];
    if (bracket && familyQuotient >= bracket.min) {
      return bracket.rate;
    }
  }

  return 0;
};

// ponytail: shares ≥ 2 → couple (2 adults), else single. Wrong for a single
// parent with 2+ children (also 2 shares); rare, acceptable for an indicative
// sim. Add an "adults" field only if that edge must be exact.
const estimateAdults = (taxSharesHousehold: number): number =>
  taxSharesHousehold >= 2 ? 2 : 1;

/**
 * Low-income rebate ("décote"): reduces the tax by `threshold − rate × tax` when
 * that is positive (i.e. only for small tax amounts). Threshold depends on
 * single vs couple.
 */
const applyDecote = (tax: number, adults: number): number => {
  const threshold = adults >= 2 ? DECOTE.couple : DECOTE.single;
  const decote = Math.max(0, threshold - DECOTE.rate * tax);
  return Math.max(0, tax - decote);
};

/**
 * Family-quotient cap ("plafonnement du quotient familial"): the tax reduction
 * from the half-parts beyond the base (1 single / 2 couple) is limited. Compares
 * the tax at the real shares to the tax at the base shares and caps the gap at
 * `half-parts × QUOTIENT_CAP_PER_HALF_PART`.
 */
// ponytail: `adults` is the base (from estimateAdults). Case T (parent isolé,
// enhanced first-half-part cap) and the special half-parts (invalidité…) are not
// modelled — over-caps a single parent's benefit. Rare, acceptable here.
const applyQuotientCap = ({
  fullTax,
  householdAnnualIncome,
  taxSharesHousehold,
  adults,
}: {
  fullTax: number;
  householdAnnualIncome: number;
  taxSharesHousehold: number;
  adults: number;
}): number => {
  if (taxSharesHousehold <= adults) return fullTax;
  const baseTax = calculateTotalTax({
    householdAnnualIncome,
    taxSharesHousehold: adults,
  });
  const halfParts = (taxSharesHousehold - adults) / 0.5;
  const cap = halfParts * QUOTIENT_CAP_PER_HALF_PART;
  const advantage = baseTax - fullTax;
  return advantage > cap ? baseTax - cap : fullTax;
};

/**
 * PER deduction ceiling ("plafond épargne retraite"), all N-1..N-3 carry-over
 * included: 10% of professional income per adult, floored at 10% of the PASS
 * and capped at 10% × 8 PASS. Household income is split across the adults, whose
 * count is estimated from the tax shares.
 */
const computeDeductionCeiling = ({
  householdAnnualIncome,
  taxSharesHousehold,
}: TaxSavingsSimulatorFormSchema): number => {
  const adults = estimateAdults(taxSharesHousehold);
  const floor = PER_DEDUCTION.rate * PER_DEDUCTION.pass;
  const cap = PER_DEDUCTION.rate * 8 * PER_DEDUCTION.pass;
  const perAdult = Math.min(
    cap,
    Math.max(floor, (PER_DEDUCTION.rate * householdAnnualIncome) / adults)
  );
  // ponytail: full N-1..N-3 carry-over assumed (first-time subscriber). Drop the
  // (1 + reportYears) factor, or expose a "carry-over" field, if the sim must
  // reflect an already-used PER.
  return perAdult * adults * (1 + PER_DEDUCTION.reportYears);
};

/**
 * Income tax for a taxable income: progressive brackets, then the family-quotient
 * cap, then the décote (impots.gouv order), then the recovery threshold.
 */
const computeIncomeTax = (
  householdAnnualIncome: number,
  taxSharesHousehold: number,
  adults: number
): number => {
  const brackets = calculateTotalTax({
    householdAnnualIncome,
    taxSharesHousehold,
  });
  const capped = applyQuotientCap({
    fullTax: brackets,
    householdAnnualIncome,
    taxSharesHousehold,
    adults,
  });
  const tax = applyDecote(capped, adults);
  // Tax below the recovery threshold is not collected.
  return tax < TAX_RECOVERY_THRESHOLD ? 0 : tax;
};

export const computeTaxSavingResults = ({
  householdAnnualIncome = 0,
  taxSharesHousehold = 1,
  deposit = 0,
}: TaxSavingsSimulatorFormSchema & { deposit?: number }) => {
  const adults = estimateAdults(taxSharesHousehold);
  const taxWithoutPer = computeIncomeTax(
    householdAnnualIncome,
    taxSharesHousehold,
    adults
  );
  const tmi = calculateTmi({
    householdAnnualIncome,
    taxSharesHousehold,
  });

  // The tax saving is the difference the PER deposit makes once deducted from the
  // taxable income and re-run through the full tax computation (brackets +
  // quotient cap + décote). This handles bracket crossing and can never be
  // negative nor exceed the tax due, so the old `deductible × TMI` formula and
  // its Math.min clamp are both gone. The deposit is capped by the PER deduction
  // ceiling before deduction.
  // ponytail: householdAnnualIncome is taken as the net taxable income (after the
  // salary allowance); the CEHR on very high incomes is not modelled (it cancels
  // in the saving). Add the CEHR only if the absolute tax must match impots.gouv
  // above ~250 k€.
  const deductible = Math.min(
    deposit,
    computeDeductionCeiling({ householdAnnualIncome, taxSharesHousehold })
  );
  const taxWithPer = computeIncomeTax(
    Math.max(0, householdAnnualIncome - deductible),
    taxSharesHousehold,
    adults
  );

  return {
    tmi,
    taxWithoutPer,
    taxWithPer,
    taxSavings: taxWithoutPer - taxWithPer,
  };
};
